// @flow
import type { DictionaryType } from "../types/GeneralTypes";

export const bannerStyles = (theme: DictionaryType): DictionaryType => ({
  banner: {
    width: "calc(100% - 32px)",
    height: "auto",
    margin: "16px",
    "&$error": {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white
    },
    "&$warning": {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.common.white
    },
    "&$info": {
      backgroundColor: theme.palette.grey.light,
      color: theme.palette.common.black
    }
  },
  error: {},
  warning: {},
  info: {},
  box: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    transition: "color 0.25s ease-in-out",
    whiteSpace: "pre-wrap",
    padding: "16px",
    "& $content": {
      color: "inherit",
      fontSize: "0.9375rem",
      fontWeight: "500",
      lineHeight: "1.5em"
    }
  },
  content: {}
});

export type BannerStylesClassesType = {
  banner: DictionaryType,
  error: DictionaryType,
  warning: DictionaryType,
  info: DictionaryType,
  box: DictionaryType,
  content: DictionaryType
};

export const panelStyles = (theme: DictionaryType): DictionaryType => ({
  appWrapper: {
    display: "block",
    margin: "0 auto",
    width: "100%"
  },
  appMain: {
    width: "100%",
    height: "100%"
  },
  appPanel: {
    minHeight: "400px",
    background: theme.palette.common.white,
    border: "none",
    clear: "both",
    display: "block",
    margin: "24px 0 0",
    padding: 0,
    position: "relative",
    width: "100%"
  },
  appPanelContent: {
    display: "block",
    width: "100%",
    height: "auto"
  },
  appPanelTop: {
    marginTop: "25px"
  },
  "@media screen and (max-width:959px)": {
    appMain: {
      marginTop: "65px"
    }
  }
});

export type PanelStylesClassesType = {
  appWrapper: DictionaryType,
  appMain: DictionaryType,
  appPanel: DictionaryType,
  appPanelContent: DictionaryType,
  appPanelTop: DictionaryType,
  content: DictionaryType
};

export const tableStyles = (theme: DictionaryType): DictionaryType => ({
  appTable: {
    overflow: "auto",
    width: "100%",
    "& tr": {
      "&$active": {
        background: "#e0e0e0",
        color: "rgba(0,0,0,.87)"
      }
    },
    "&$noBorder": {
      border: "none",
      "& tr, & th, & td": {
        border: "none"
      }
    },
    "&$lightBorder": {
      border: "1px solid " + theme.palette.grey.light,
      "& tr, & th, & td": {
        border: "1px solid " + theme.palette.grey.light
      }
    },
    "&$center": {
      "& th, & td": {
        textAlign: "center"
      }
    },
    "&$colorTop": {
      borderTop: ".2em solid " + theme.palette.primary.light
    },
    "& $border": {
      fontSize: "0.875rem",
      fontWeight: 600
    },
    "& $normal": {
      fontSize: "0.875rem"
    },
    "& $compact": {
      padding: "5px"
    },
    "& $short": {
      paddingLeft: "5px"
    },
    "& $slim": {
      height: "32px"
    },
    "& $top": {
      verticalAlign: "top"
    },
    "& $light": {
      color: theme.palette.grey.light
    },
    "& $inlineDivider": {
      borderTop: "1px solid " + theme.palette.primary.light,
      height: "24px"
    },
    "& $blueShine": {
      color: theme.palette.blue.main,
      fontSize: "0.9375rem"
    }
  },
  noBorder: {},
  lightBorder: {},
  center: {},
  colorTop: {},
  active: {},
  border: {},
  normal: {},
  compact: {},
  short: {},
  slim: {},
  top: {},
  light: {},
  inlineDivider: {},
  blueShine: {},
  "@media screen and (max-width:959px)": {
    appTable: {
      "& tr": {
        width: "auto !important",
        display: "block !important",
        height: "auto !important"
      },
      "& th, & td": {
        width: "auto !important",
        display: "block !important",
        minHeight: "30px"
      }
    }
  }
});

export type TableStylesClassesType = {
  appTable: DictionaryType,
  active: DictionaryType,
  noBorder: DictionaryType,
  lightBorder: DictionaryType,
  center: DictionaryType,
  colorTop: DictionaryType,
  border: DictionaryType,
  normal: DictionaryType,
  compact: DictionaryType,
  short: DictionaryType,
  slim: DictionaryType,
  top: DictionaryType,
  light: DictionaryType,
  inlineDivider: DictionaryType,
  blueShine: DictionaryType
};

const ToolBarHeight = 80;
const DrawWidth = 240;
const DrawCloseWidth = 64;
const DrawWideWidth = 520;

export const drawerStyles = (theme: DictionaryType): DictionaryType => ({
  root: {
    flexGrow: 1,
    height: "100vh",
    zIndex: 1,
    overflow: "auto",
    position: "relative",
    display: "flex"
  },
  subRoot: {
    marginTop: "-24px",
    height: "calc(100vh - 81px)"
  },
  appBar: {
    top: "0",
    left: "auto",
    right: "0",
    position: "absolute",
    zIndex: 1600,
    width: "100%",
    height: ToolBarHeight,
    display: "flex",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  appBarShift: {
    marginLeft: DrawWidth,
    width: `calc(100% - ${DrawWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  appBarShiftWide: {
    marginLeft: DrawWideWidth,
    width: `calc(100% - ${DrawWideWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  appBarHeader: {
    width: "100%",
    marginLeft: DrawCloseWidth
  },
  appBarHeaderOpen: {
    marginLeft: 0
  },
  appBarHeaderWide: {
    width: "100%",
    marginLeft: DrawWidth
  },
  appBarHeaderWideOpen: {
    marginLeft: 0
  },
  menuButton: {
    marginLeft: 12,
    marginRight: 12
  },
  hide: {
    display: "none !IMPORTANT"
  },
  drawerPaper: {
    position: "relative",
    width: DrawWidth,
    zIndex: 1800,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerPaperWide: {
    width: DrawWideWidth
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: DrawCloseWidth
  },
  drawerPaperCloseWide: {
    width: DrawWidth
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    height: ToolBarHeight,
    position: "fixed",
    width: "inherit",
    borderBottom: "1px solid " + theme.palette.grey.light,
    zIndex: 1200
  },
  toolbarSearch: {
    display: "inherit",
    width: "inherit"
  },
  toolbarInside: {
    width: DrawWidth - 2,
    marginLeft: 1,
    marginRight: 1
  },
  content: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing.unit * 3,
    marginTop: ToolBarHeight
  },
  wrap: {
    position: "absolute",
    left: DrawWidth + theme.spacing.unit * 3,
    right: theme.spacing.unit * 3,
    top: ToolBarHeight,
    bottom: 0,
    padding: theme.spacing.unit * 1,
    width: "inherit",
    overflowY: "auto",
    overflowX: "hidden"
  },
  wrapWide: {
    left: DrawWideWidth + theme.spacing.unit * 3
  },
  wrapClose: {
    left: theme.spacing.unit * 11
  },
  wrapCloseWide: {
    left: DrawWidth + theme.spacing.unit * 3
  },
  item: {},
  itemCenter: {
    textAlign: "center"
  },
  itemSelected: {
    backgroundColor: theme.palette.grey.light
  },
  itemHidden: {
    paddingLeft: "2px",
    paddingRight: "2px",
    "& $itemTitle": {
      textOverflow: "clip !IMPORTANT"
    }
  },
  itemTitle: {
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  itemTitleSelected: {
    fontWeight: 800
  },
  itemTitleUnselected: {
    color: theme.palette.grey.main,
    opacity: "0.7"
  },
  itemTitleNoPadding: {
    paddingLeft: 0,
    paddingRight: 0
  },
  list: {
    marginTop: ToolBarHeight,
    zIndex: 1000,
    overflowY: "auto",
    overflowX: "hidden",
    height: "100%"
  },
  listSub: {
    height: `calc(100% - ${ToolBarHeight}px)`
  },
  leftPanel: {
    textAlign: "center",
    position: "absolute",
    top: 0,
    bottom: 0,
    height: "100%",
    left: 0,
    width: DrawWidth,
    overflowY: "auto",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderRightColor: theme.palette.grey.light
  },
  rightPanel: {
    position: "absolute",
    top: 0,
    bottom: 0,
    height: "100%",
    left: DrawWidth + theme.spacing.unit * 2,
    right: 0,
    overflowY: "auto"
  }
});

export type DrawerStylesClassesType = {
  root: DictionaryType,
  subRoot: DictionaryType,
  appBar: DictionaryType,
  appBarShift: DictionaryType,
  appBarShiftWide: DictionaryType,
  appBarHeader: DictionaryType,
  appBarHeaderOpen: DictionaryType,
  appBarHeaderWide: DictionaryType,
  appBarHeaderWideOpen: DictionaryType,
  menuButton: DictionaryType,
  hide: DictionaryType,
  hidden: DictionaryType,
  drawerPaper: DictionaryType,
  drawerPaperClose: DictionaryType,
  drawerPaperWide: DictionaryType,
  drawerPaperCloseWide: DictionaryType,
  toolbar: DictionaryType,
  toolbarSearch: DictionaryType,
  toolbarInside: DictionaryType,
  content: DictionaryType,
  wrap: DictionaryType,
  wrapClose: DictionaryType,
  wrapWide: DictionaryType,
  wrapCloseWide: DictionaryType,
  item: DictionaryType,
  itemCenter: DictionaryType,
  itemSelected: DictionaryType,
  itemHidden: DictionaryType,
  itemTitle: DictionaryType,
  itemTitleSelected: DictionaryType,
  itemTitleUnselected: DictionaryType,
  itemTitleNoPadding: DictionaryType,
  list: DictionaryType,
  listSub: DictionaryType,
  leftPanel: DictionaryType,
  rightPanel: DictionaryType
};

export const headerStyles = (theme: DictionaryType): DictionaryType => ({
  appHeader: {
    borderBottom: "1px solid " + theme.palette.primary.main,
    padding: "24px 10px"
  },
  headerWrapper: {
    height: "32px",
    alignItems: "flex-start",
    display: "inline-flex"
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontStyle: "inherit",
    fontWeight: "500",
    letterSpacing: "-0.01em",
    lineHeight: "32px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  actionItem: {
    flex: "1 0 auto"
  }
});

export type HeaderStylesClassesType = {
  appHeader: DictionaryType,
  headerWrapper: DictionaryType,
  headerTitle: DictionaryType,
  actionItem: DictionaryType
};

export const colorLabel = {
  borderRadius: "2em",
  display: "inline-block",
  padding: "2px 12px"
};

export const redLabel = {
  ...colorLabel,
  backgroundColor: "#FF5630",
  color: "#FFFFFF"
};

export const pinkLabel = {
  ...colorLabel,
  backgroundColor: "#FFEBE6",
  color: "#BF2600"
};

export const greenLabel = {
  ...colorLabel,
  backgroundColor: "#39b54a",
  color: "#FFFFFF"
};

export const blueLabel = {
  ...colorLabel,
  backgroundColor: "#23BEBE",
  color: "#FFFFFF"
};

export const darkLabel = {
  ...colorLabel,
  backgroundColor: "#286d32",
  color: "#FFFFFF"
};

export const greyLabel = {
  ...colorLabel,
  backgroundColor: "#8a8a8a",
  color: "#FFFFFF"
};

export const lightGreyLabel = {
  ...colorLabel,
  backgroundColor: "#dddddd",
  color: "#000000"
};

export const formText = {
  width: "100%",
  textAlign: "center"
};

export const topBanner = {
  position: "fixed",
  top: "5%",
  left: "10%",
  width: "80%",
  zIndex: 99999999
};

export const padImg = {
  width: "35px",
  height: "35px"
};

export const formRightBtnBlock = {
  width: "auto",
  display: "flex",
  justifyContent: "flex-end",
  margin: "0 2% 0 2%"
};

export const boxShadow = {
  boxShadow:
    "0 10px 30px -12px rgba(0, 0, 0, 0.42), 0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)"
};

export const expansionPanelHeader = {
  flexBasis: "33.33%",
  flexShrink: 0
};
